import { REPLACE_TEST_ENTRIES } from "../../../store/actions";
import { API_URLS } from "../../../utils/api-constants";
import { axiosPost } from "../../../utils/queries";
import {
  ATTRIBUTES_SUB_URL,
  MAX_ATTRIBUTES_COUNT,
  PUBLIC_TEST_URL,
  SPECIAL_ATTRIBUTES_SUB_URL,
  INTRO_SUB_URL,
  INTRO_MODULE_SUB_URL,
  PERSONAL_SUMMARY_SUB_URL,
} from "../constants";
import { ModuleEnum, Module } from "../../../constants";
import { DtoEntry, DtoPublicTest, DtoTestSet } from "@/types/dto-types";
import { Category } from "../FuturebilityCockpit/types";
import { StaticIntroType } from "./types";
import { ReflectionType } from "../../../types";

export const buildUrlWithGetParams = (url: string) => {
  const queryString = window.location.search;
  return `${url}${queryString}`;
};

const buildIntroModuleUrl = (
  testId: string,
  sessionId: string,
  introType: StaticIntroType
) => {
  return buildUrlWithGetParams(
    `${PUBLIC_TEST_URL}/${testId}/${sessionId}/${INTRO_MODULE_SUB_URL}/${introType}`
  );
};

/**
 * Helper function to check if a category/type is ready for intro page
 * Returns true if there are uncompleted test sets but none completed (i.e., not started yet)
 */
const isReadyForIntro = <T>(
  completedTestSets: DtoTestSet[],
  uncompletedTestSets: DtoTestSet[],
  predicate: (testSet: DtoTestSet) => T | undefined,
  value: T
): boolean => {
  const hasUncompleted = uncompletedTestSets.some(
    ts => predicate(ts) === value
  );
  const hasCompleted = completedTestSets.some(ts => predicate(ts) === value);
  return hasUncompleted && !hasCompleted;
};

/**
 * Helper function to check if all previous items in the list are fully completed
 * Previous category or type
 */
const arePreviousCategoryOrTypeItemsCompleted = <T>(
  uncompletedTestSets: DtoTestSet[],
  items: Array<{ value: T }>,
  currentIndex: number,
  predicate: (testSet: DtoTestSet) => T | undefined
): boolean => {
  for (let j = 0; j < currentIndex; j++) {
    const hasUncompleted = uncompletedTestSets.some(
      ts => predicate(ts) === items[j].value
    );
    if (hasUncompleted) {
      return false;
    }
  }
  return true;
};

/**
 * Determines the appropriate intro module URL based on the current module and test set completion status
 * @param completedTestSets - Array of completed test sets
 * @param uncompletedTestSets - Array of uncompleted test sets
 * @param currentModule - The current module being accessed
 * @param testId - The test ID
 * @param sessionId - The session ID
 * @returns The intro module URL if all previous category test sets are completed and current category has none completed, undefined otherwise
 */
const getIntroModuleUrlByCategory = (
  completedTestSets: DtoTestSet[],
  uncompletedTestSets: DtoTestSet[],
  currentModule: ModuleEnum,
  testId: string,
  sessionId: string
): string | undefined => {
  if (currentModule === Module.Analytics) {
    // Define mapping between test set categories and their corresponding intro pages
    // The order matters: Organization -> Team -> Leadership
    const categoryMap = [
      { value: Category.Organization, introType: StaticIntroType.Organisation },
      { value: Category.Team, introType: StaticIntroType.Team },
      { value: Category.Leadership, introType: StaticIntroType.Leadership },
    ];

    // Check each category in order
    for (let i = 0; i < categoryMap.length; i++) {
      const { value: category, introType } = categoryMap[i];

      // Check if this category is ready for intro (has uncompleted but no completed test sets)
      if (
        isReadyForIntro(
          completedTestSets,
          uncompletedTestSets,
          ts => ts.category,
          category
        ) &&
        arePreviousCategoryOrTypeItemsCompleted(
          uncompletedTestSets,
          categoryMap,
          i,
          ts => ts.category
        )
      ) {
        return buildIntroModuleUrl(testId, sessionId, introType);
      }
    }
  } else if (currentModule === Module.Reflection) {
    // Define mapping between reflection types and their corresponding intro pages
    // The order matters: Leverage -> Challenge
    const reflectionTypeMap = [
      { value: ReflectionType.Leverage, introType: StaticIntroType.Leverages },
      {
        value: ReflectionType.Challenge,
        introType: StaticIntroType.Challenges,
      },
    ];

    // Check each reflection type in order
    for (let i = 0; i < reflectionTypeMap.length; i++) {
      const { value: type, introType } = reflectionTypeMap[i];

      // Check if this type is ready for intro (has uncompleted but no completed test sets)
      if (
        isReadyForIntro(
          completedTestSets,
          uncompletedTestSets,
          ts => ts.info?.type,
          type
        ) &&
        arePreviousCategoryOrTypeItemsCompleted(
          uncompletedTestSets,
          reflectionTypeMap,
          i,
          ts => ts.info?.type
        )
      ) {
        return buildIntroModuleUrl(testId, sessionId, introType);
      }
    }
  }

  return undefined;
};

export const getParticipationFlowUrlForCurrentSession = (
  testData: DtoPublicTest,
  sessionId: string,
  currentModule: ModuleEnum
) => {
  const { _id, entries, sets } = testData;
  if (sets.length === 0) {
    // Not valid case, show home page
    return "/";
  }

  if (!entries.length) {
    return buildUrlWithGetParams(
      `${PUBLIC_TEST_URL}/${_id}/${sessionId}/${currentModule}/${INTRO_SUB_URL}`
    );
  }

  let currentModuleTetSets: DtoTestSet[] = [];

  if (currentModule === Module.Reflection) {
    currentModuleTetSets = testData.reflectionTestSets;
  } else if (currentModule === Module.Solution) {
    currentModuleTetSets = testData.solutionTestSets;
  } else {
    currentModuleTetSets = testData.sets;
  }

  const uncompletedTestSets = getUncompletedTestSets(
    currentModuleTetSets,
    testData.entries
  );
  const completedTestSets = getCompletedTestSets(
    currentModuleTetSets,
    testData.entries
  );

  if (uncompletedTestSets.length > 0) {
    const introModuleUrl = getIntroModuleUrlByCategory(
      completedTestSets,
      uncompletedTestSets,
      currentModule,
      _id,
      sessionId
    );

    if (introModuleUrl) {
      return introModuleUrl;
    }

    const {
      info: uncompletedTestSetInfo,
      _id: uncompletedTestSetId,
      items: uncompletedTestSetItems,
    } = uncompletedTestSets[0] ?? {};
    const subStep = uncompletedTestSetInfo?.infoParticipation
      ? INTRO_SUB_URL
      : uncompletedTestSetItems[0]?._id;
    return buildUrlWithGetParams(
      `${PUBLIC_TEST_URL}/${_id}/${sessionId}/${currentModule}/${uncompletedTestSetId}/${subStep}`
    );
  } else if (
    currentModule === Module.Analytics &&
    testHasSpecialAttributes(testData) &&
    !hasAllSpecialAttributesFilledIn(testData)
  ) {
    return buildUrlWithGetParams(
      `${PUBLIC_TEST_URL}/${_id}/${sessionId}/${currentModule}/${SPECIAL_ATTRIBUTES_SUB_URL}`
    );
  } else if (
    currentModule === Module.Analytics &&
    testHasRegularAttributes(testData) &&
    !hasAllRegularAttributesFilledIn(testData)
  ) {
    return buildUrlWithGetParams(
      `${PUBLIC_TEST_URL}/${_id}/${sessionId}/${currentModule}/${ATTRIBUTES_SUB_URL}`
    );
  } else {
    if (currentModule === Module.Analytics) {
      return buildUrlWithGetParams(
        `${PUBLIC_TEST_URL}/${_id}/${sessionId}/${INTRO_MODULE_SUB_URL}/${StaticIntroType.PersonalSummary}`
      );
    }

    return buildUrlWithGetParams(
      `${PUBLIC_TEST_URL}/${_id}/${sessionId}/${currentModule}/${PERSONAL_SUMMARY_SUB_URL}`
    );
  }
};

export const getUncompletedTestSets = (
  sets: DtoTestSet[],
  entries: DtoEntry[]
) => {
  const completedEntries = entries.filter(entry => entry.completed);
  return sets.filter(
    set => !completedEntries.find(entry => entry.testSetId === set._id)
  );
};

export const getCompletedTestSets = (
  sets: DtoTestSet[],
  entries: DtoEntry[]
) => {
  const completedEntries = entries.filter(entry => entry.completed);
  return sets.filter(set =>
    completedEntries.find(entry => entry.testSetId === set._id)
  );
};

export const testHasUncompletedTestSets = (
  sets: DtoTestSet[],
  entries: DtoEntry[]
) => {
  return !!getUncompletedTestSets(sets, entries).length;
};

export const testHasAttributes = (
  testData: DtoPublicTest,
  specialAttributes: boolean
) => {
  return attributesMapper.some(
    index =>
      // @ts-ignore
      testData[
        specialAttributes
          ? `specialAttribute${index}Label`
          : `attribute${index}Label`
      ]
  );
};

export const testHasRegularAttributes = (testData: DtoPublicTest) => {
  return testHasAttributes(testData, false);
};

export const testHasSpecialAttributes = (testData: DtoPublicTest) => {
  return testHasAttributes(testData, true);
};

export const getUnAnsweredQuestionIndex = (
  currentSet: DtoTestSet,
  entries: DtoEntry[]
) => {
  const allItemsOptional = currentSet.items.every(item => !item.mandatory);

  if (allItemsOptional) {
    return "";
  }

  const index = currentSet.items.findIndex(
    // @ts-ignore
    item => !entries || (item.mandatory && !entries[item.dbColumn])
  );

  return index === -1 ? "" : index;
};

// @ts-ignore
export const attributesMapper = [...Array(MAX_ATTRIBUTES_COUNT).keys()].map(
  i => i + 1
); // Currently only 3 attributes supported

export const getAvailableAttributes = (
  testData: DtoPublicTest,
  specialAttributes: boolean
) => {
  return attributesMapper
    .map(i => {
      const labelKey = specialAttributes
        ? `specialAttribute${i}Label`
        : `attribute${i}Label`;
      const valueKey = specialAttributes
        ? `specialAttribute${i}Value`
        : `attribute${i}Value`;
      // @ts-ignore
      const label = testData[labelKey];
      // @ts-ignore
      const values = testData[valueKey];

      if (label && values) {
        return specialAttributes ? `specialAttribute${i}` : `attribute${i}`;
      }

      return false;
    })
    .filter(item => item);
};

export const hasAllAttributesFilledIn = (
  testData: DtoPublicTest,
  specialAttributes: boolean
) => {
  const availableAttributes = getAvailableAttributes(
    testData,
    specialAttributes
  );
  return testData.entries.every(testEntry =>
    // @ts-ignore
    availableAttributes.every(attr => testEntry[attr])
  );
};

export const hasAllRegularAttributesFilledIn = (testData: DtoPublicTest) => {
  return hasAllAttributesFilledIn(testData, false);
};

export const hasAllSpecialAttributesFilledIn = (testData: DtoPublicTest) => {
  return hasAllAttributesFilledIn(testData, true);
};

export const canStartTest = (testData: DtoPublicTest) => {
  const {
    completedTestFlowsCount = 0,
    isActive = true,
    maxReplies = 0,
    group: { participantsLimit } = { participantsLimit: 0 },
  } = testData;

  const testMaxRepliesThreshold =
    maxReplies === 0 ||
    (maxReplies > 0 && maxReplies > completedTestFlowsCount);

  const groupMaxRepliesThreshold =
    participantsLimit === 0 ||
    (participantsLimit > 0 && participantsLimit > completedTestFlowsCount);

  return isActive && testMaxRepliesThreshold && groupMaxRepliesThreshold;
};

export const getGroupIdFromUrl = () => {
  return new URLSearchParams(window.location.search).get("groupId");
};

export const saveAttributes = ({
  publicTest,
  sessionId,
  currentAttributes,
  state,
  dispatch,
  history,
  currentModule,
}: any) => {
  const data = {
    testId: publicTest._id,
    sessionId: sessionId,
    ...currentAttributes,
  };

  axiosPost(API_URLS.PUBLIC_TEST_ATTRIBUTES, { data }).then(payload => {
    dispatch({ type: REPLACE_TEST_ENTRIES, payload });
    history.push(
      getParticipationFlowUrlForCurrentSession(
        { ...state.publicTest, entries: payload },
        sessionId,
        currentModule
      )
    );
  });
};
